import React, { useRef, useState } from "react";
import { Button } from "aible";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { LineInput } from "components/Inputs/LineInput";
import Tooltip from "components/Inputs/Tooltip";
import sparklesImg from "images/sparkles.svg";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import { isEnabled } from "utils/featureFlags";
import ChatGPTContainer, { DatasetAnalytics, WhatsChangedInput } from "components/ChatGPT/ChatGPT";
import BlueprintSelector from "components/ChatGPT/BlueprintSelector";
import ToneSelector from "components/ChatGPT/ToneSettings/ToneSelector";
import ChatGPTSettings from "components/ChatGPT/ChatGPTSettings";
import SmallDateChart from "components/Tiles/Sense/SmallDateChart";
import DynamicWidthSelector from "components/ChatGPT/DynamicWidthSelector";
import styles from "./style.module.css";
import ModelSelector from "components/ChatGPT/ModelSelector";
import DocumentSelector from "components/LanguageModelArtifact/DocumentSelector";
import DatasetSelector from "components/Dataset/DatasetSelector";
import SqlWarningPopup from "components/ChatGPT/SqlWarningPopup";
import LegacyModelWarning from "components/ChatGPT/LegacyModelWarning";
import WarningBanner from "containers/WarningBanner/WarningBanner";
import ChatTourContainer from "containers/Tour/ChatTour";
import ContextSelectionPanel from "components/NewChat/ContextSelectionPanel";
import ContextSelectionButtons from "components/NewChat/ContextSelectionButtons";
import AgentAutonomyControls from "components/AgentAutonomy/AgentAutonomyControls";
import NewChatSimpleHeader from "components/NewChat/NewChatSimpleHeader";
import AibleAgent from "components/AibleAgent/AibleAgent";
import TopSampleTemplates from "components/NewChat/TopSampleTemplates";
import { RightPanel as TemplatesRightPanel } from "components/View/TemplatesView";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { clsx } from "clsx";
import PenIcon from "images/pen.svg";
import AgentIqToolOptionsList from "components/NewChat/AgentIqToolOptionsList";
import { useDispatch, useSelector, wrapDispatch } from "@/types/store";
import { getComparisonOptions, getDatasetId, updateComparisonOptions, updateDatasetId } from "@/containers/Next/NextReducer";
import { changeTimestampColumn, getDataset } from "@/containers/Home/reducer";
import { askMultiModels, getAgentIQBaseModel, getAgentIQNLQModel, getAgentIQReasoningModel, getAgentIQTools, getArtifactId, getBackgroundColorDarkMode, getBlueprintId, getChatApp, getChatButtonDarkMode, getChatClearInput, getChatTone, getChatToneOptions, getChatType, getContextSelectionPanelTab, getConversationArray, getFocusVariable, getFocusVariablePrompt, getIsAsking, getIsAskingAgent, getIsAskingSuggestQuestions, getIsWebSearch, getLinkColorDarkMode, getModelId, getNewChatKPIPanel, getReplayPrompts, getShowSettings, getUseFocusVariable, handleInit, initAskForDataset, initSuggestQuestions, updateArtifactId, updateBlueprintId, updateChatTone, updateContextSelectionPanelTab, updateFocusVariable, updateIsWebSearch, updatePrompt, updateShowSettings } from "@/containers/ChatGPT/reducer";
import { getAgentSimpleAnalyticsView } from "@/containers/View/reducer";
import { selectOnChangeWrapper } from "@/utils/select";
import { getModels } from "@/containers/AiService/reducer";
import { getAgentAutonomyView, getConvoForReplay, getIsMac, getType } from "@/containers/View/reducer";
import { getCurrentStep } from "@/containers/ChatApp/reducer";
import * as aibleAnywhereReducer from "containers/AibleAnywhere/reducer";
import NextSearchContainer from "containers/Next/NextSearch";
const FakeChat = () => {
    return (React.createElement("div", { className: styles.noDatasetChat },
        React.createElement(ChatGPTContainer, { isNoDataset: true })));
};
const stringToSpans = (string, classOverride = undefined) => {
    if (string === undefined) {
        return React.createElement(React.Fragment, null);
    }
    return string.split(" ").map((word, index, array) => {
        if (index === array.length - 1) {
            return (React.createElement("span", { key: index + "_" + word, className: classOverride ? classOverride : styles.headerText }, `${word}  `));
        }
        else {
            return (React.createElement("span", { key: index + "_" + word, className: classOverride ? classOverride : styles.headerText }, `${word} `));
        }
    });
};
const FocusVariableInput = () => {
    const dispatch = useDispatch();
    const useFocusVariable = useSelector(getUseFocusVariable);
    const focusVariable = useSelector(getFocusVariable);
    const focusVariablePrompt = useSelector(getFocusVariablePrompt);
    const focusVariablePlaceholder = useSelector(getFocusVariablePrompt);
    if (!useFocusVariable) {
        return React.createElement(React.Fragment, null);
    }
    return (React.createElement(React.Fragment, null,
        stringToSpans(focusVariablePrompt),
        React.createElement(LineInput, { value: focusVariable, placeholder: focusVariablePlaceholder, onChange: wrapDispatch(dispatch, updateFocusVariable) })));
};
const Header = ({ nextId, inputBoxRef, usecaseSelectorRef, isWhatsChangedAnalysisLoading, updateIsWhatsChangedAnalysisLoading }) => {
    const dispatch = useDispatch();
    const datasetId = useSelector((state) => getDatasetId(state, nextId));
    const comparisonOptions = useSelector((state) => getComparisonOptions(state, nextId));
    const artifactId = useSelector(getArtifactId);
    const backgroundColorDarkMode = useSelector(getBackgroundColorDarkMode);
    const chatApp = useSelector(getChatApp);
    const chatType = useSelector(getChatType);
    const dataset = useSelector((state) => getDataset(state, datasetId));
    const dateFieldOptions = dataset?.ranked_fields !== undefined
        ? dataset.ranked_fields
            .filter((f) => f.type === "date")
            .map((f) => ({
            value: f.label,
            label: f.label
        }))
        : [];
    const datasetDateField = dataset?.options?.timestamp_column;
    const introMessage = "I am ready to chat about";
    const docSumMessage = "to answer your summarization questions.";
    const nlqMessage = "to answer your NLQ questions.";
    const analyticsMessage = "to answer your analytics questions.";
    const questionsMessage = "to answer your questions.";
    const whatsChangedMessage = "by the dates in";
    const whatsChangedDateMessage = "to summarize changes" + (comparisonOptions?.referenceStart ? ` after ${comparisonOptions.referenceStart}.` : ".");
    const scanMessage = "I will scan";
    const dataAnalyzingMessage = "This dataset is being analyzed.";
    const dataReanalyzeMessage = "This dataset will need to be reanalyzed to see what's changed.";
    const noDateOptionsMessage = "Warning: This dataset does not have date options for a What's Changed analysis.";
    const isProcessing = dataset && dataset?.status_name !== "DONE";
    return (React.createElement(React.Fragment, null,
        isEnabled("chat-aible-beta-warning") && React.createElement(WarningBanner, { type: "chat_aible_beta_warning" }),
        React.createElement("div", { className: styles.mainBox, style: {
                color: backgroundColorDarkMode ? "#EFECF6" : "#9E9DBC"
            } },
            React.createElement("div", { className: styles.headerWrapper },
                React.createElement("div", { className: styles.headerText }, "Hello!"),
                React.createElement("div", { className: styles.promptWrapper },
                    React.createElement("div", { className: styles.promptRow },
                        stringToSpans(introMessage),
                        React.createElement(BlueprintSelector, { menuPlacement: "bottom", isStyled: true, isNewChat: true, isDynamic: true, color: chatApp?.wizard?.linkColor, style: { marginLeft: "-9px" }, skipSearchFilter: true, innerRef: usecaseSelectorRef, onChangeFocus: inputBoxRef }),
                        React.createElement("span", { className: styles.headerText }, "with "),
                        React.createElement("span", { className: styles.headerText }, "a  "),
                        React.createElement(ToneSelector, { menuPlacement: "bottom", isStyled: true, isNewChat: true, isDynamic: true, color: chatApp?.wizard?.linkColor, style: { marginLeft: "-9px" } }),
                        React.createElement("span", { className: styles.headerText }, "tone "),
                        React.createElement("span", { className: styles.headerText }, "using  "),
                        React.createElement(ModelSelector, { menuPlacement: "bottom", isStyled: true, isNewChat: true, isDynamic: true, color: chatApp?.wizard?.linkColor ? chatApp?.wizard?.linkColor : "#9F85F2", style: { marginLeft: "-9px" } }),
                        isEnabled("chataible-focus-variable") && React.createElement(FocusVariableInput, null)),
                    chatType === "DOCUMENT" && (React.createElement("div", { className: styles.promptRow, style: {
                            marginTop: "20px"
                        }, id: "chat-data-select" },
                        stringToSpans(scanMessage),
                        React.createElement(DocumentSelector, { artifactId: artifactId, onChange: (val) => dispatch(updateArtifactId(val?.id)), allowUpload: true, menuPlacement: "bottom", isStyled: true, isNewChat: true, isDynamic: true, color: chatApp?.wizard?.linkColor, style: { marginLeft: "-9px" } }),
                        stringToSpans(docSumMessage))),
                    (chatType === "AIBLE" || chatType === "SQL" || chatType === "WHATS_CHANGED") && (React.createElement("div", { className: styles.promptRow, style: {
                            marginTop: "20px"
                        }, id: "chat-data-select" },
                        stringToSpans(scanMessage),
                        React.createElement(DatasetSelector, { datasetId: datasetId, onChange: (val) => {
                                dispatch(updateDatasetId(val));
                            }, isDynamic: true, isNewChat: true, isStyled: true, allowAllDatasets: chatType === "SQL", filter: chatType === "WHATS_CHANGED" ? "date" : undefined, color: chatApp?.wizard?.linkColor, style: { marginLeft: "-9px" }, skipSearchFilter: true, allowUpload: true, allowOutcomeSelect: chatType === "WHATS_CHANGED" || chatType === "AIBLE" }),
                        chatType === "WHATS_CHANGED" && datasetId !== undefined && datasetId !== "UPLOAD_CSV" ? (dateFieldOptions?.length < 1 ? (dataset?.outcome && !isProcessing ? (React.createElement(React.Fragment, null,
                            React.createElement("div", { className: styles.promptRow, style: { marginTop: "12px" } }, stringToSpans(noDateOptionsMessage, styles.warningText)))) : (React.createElement(React.Fragment, null))) : (React.createElement(React.Fragment, null,
                            stringToSpans(whatsChangedMessage),
                            React.createElement(DynamicWidthSelector, { options: dateFieldOptions, onChange: selectOnChangeWrapper((v) => {
                                    if (v !== comparisonOptions?.dateField) {
                                        dispatch(updateComparisonOptions({
                                            ...comparisonOptions,
                                            dateField: v
                                        }));
                                    }
                                }), isStyled: true, isNewChat: true, isDynamic: true, 
                                // selectorStyle={{
                                //   maxWidth: showSettings
                                //     ? "clamp(40px, 50vw - 500px, 800px)"
                                //     : "clamp(40px, 100vw - 500px, 800px)",
                                // }}
                                isDisabled: !dataset, menuPlacement: "top", value: comparisonOptions?.dateField
                                    ? {
                                        label: comparisonOptions.dateField,
                                        value: comparisonOptions.dateField
                                    }
                                    : undefined, placeholder: !dataset ? "No Dataset Selected" : "Select a Date Field", color: chatApp?.wizard?.linkColor, style: { marginLeft: "-9px" } }),
                            dataset && comparisonOptions?.dateField !== undefined ? (dataset?.status_name !== "DONE" && dataset?.status_name !== "ERROR" ? (React.createElement(React.Fragment, null,
                                React.createElement("div", { className: styles.promptRow, style: { marginTop: "20px" } },
                                    stringToSpans(dataAnalyzingMessage),
                                    React.createElement("div", { style: {
                                            display: "flex",
                                            width: "100%",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            marginTop: "8px",
                                            position: "relative",
                                            height: 50
                                        } },
                                        React.createElement(LoadingIndicator, { style: {
                                                margin: "0px",
                                                transform: "scale(0.5)",
                                                position: "absolute"
                                            } }))))) : comparisonOptions?.dateField !== datasetDateField || isWhatsChangedAnalysisLoading ? (React.createElement(React.Fragment, null,
                                React.createElement("div", { className: styles.promptRow, style: { marginTop: "20px" } },
                                    stringToSpans(dataReanalyzeMessage),
                                    React.createElement("div", { style: {
                                            display: "flex",
                                            width: "100%",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            marginTop: "8px"
                                        } },
                                        React.createElement(Button, { type: "primary", onClick: () => {
                                                if (!comparisonOptions?.dateField) {
                                                    return;
                                                }
                                                updateIsWhatsChangedAnalysisLoading(true);
                                                return dispatch(changeTimestampColumn(dataset.id, comparisonOptions?.dateField))
                                                    .catch((e) => {
                                                    console.log(e);
                                                })
                                                    .finally(() => updateIsWhatsChangedAnalysisLoading(false));
                                            }, isLoading: isWhatsChangedAnalysisLoading }, "Analyze " + comparisonOptions?.dateField))))) : (React.createElement(React.Fragment, null,
                                stringToSpans(whatsChangedDateMessage),
                                React.createElement(SmallDateChart, { datasetId: dataset?.id, updateComparisonOptions: updateComparisonOptions, comparisonOptions: comparisonOptions, field: dataset?.fields?.[comparisonOptions?.dateField], hideChart: true, hideText: true, style: {
                                        width: "50%",
                                        minWidth: "300px",
                                        marginTop: "0px",
                                        marginLeft: "10px",
                                        fontSize: "16px"
                                    } })))) : (React.createElement(React.Fragment, null))))) : chatType === "SQL" ? (stringToSpans(nlqMessage)) : chatType === "AIBLE" ? (stringToSpans(analyticsMessage)) : (stringToSpans(questionsMessage)))))),
            React.createElement("div", { className: styles.chatSpacer }))));
};
const NewChat = () => {
    const nextId = "new";
    const dispatch = useDispatch();
    const [textInput, updateTextInput] = useState("");
    const [sProject, updateSProject] = useState();
    const [isWhatsChangedAnalysisLoading, updateIsWhatsChangedAnalysisLoading] = useState(false);
    const [selectedTemplate, updatedSelectedTemplate] = useState();
    const [isChatLoading, updateIsChatLoading] = useState(false);
    const [showDaterange, setShowDaterange] = useState(false);
    const inputBoxRef = useRef(null);
    const usecaseSelectorRef = useRef(null);
    const datasetId = useSelector((state) => getDatasetId(state, nextId));
    const dataset = useSelector((state) => getDataset(state, datasetId));
    const comparisonOptions = useSelector((state) => getComparisonOptions(state, nextId));
    const agentIQBaseModel = useSelector(getAgentIQBaseModel);
    const agentIQNLQModel = useSelector(getAgentIQNLQModel);
    const agentIQReasoningModel = useSelector(getAgentIQReasoningModel);
    const chatType = useSelector(getChatType);
    const artifactId = useSelector(getArtifactId);
    const blueprintId = useSelector(getBlueprintId);
    const agentIqTools = useSelector(getAgentIQTools);
    const isAsking = useSelector(getIsAsking);
    const isAskingAgent = useSelector(getIsAskingAgent);
    const chatApp = useSelector(getChatApp);
    const replayPrompts = useSelector(getReplayPrompts);
    const viewType = useSelector(getType);
    const contextSelectionPanelTab = useSelector(getContextSelectionPanelTab);
    const showSettings = useSelector(getShowSettings);
    const agentAutonomyView = useSelector(getAgentAutonomyView);
    const isAibleAnywhereLoading = useSelector(aibleAnywhereReducer.getIsLoading);
    const conversationArray = useSelector(getConversationArray);
    const convoReplay = useSelector(getConvoForReplay);
    const currentStep = useSelector(getCurrentStep);
    const isMac = useSelector(getIsMac);
    const modelId = useSelector(getModelId);
    const isAskingSuggestQuestions = useSelector(getIsAskingSuggestQuestions);
    const linkColorDarkMode = useSelector(getLinkColorDarkMode);
    const chatButtonDarkMode = useSelector(getChatButtonDarkMode);
    const isWebSearch = useSelector(getIsWebSearch);
    const chatTones = useSelector(getChatToneOptions);
    const chatTone = useSelector(getChatTone);
    const newChatKPIPanel = useSelector(getNewChatKPIPanel);
    const chatClearInput = useSelector(getChatClearInput);
    const agentSimpleAnalyticsView = useSelector((state) => getAgentSimpleAnalyticsView(state));
    const useAgentSimpleAnalytics = agentSimpleAnalyticsView;
    const isAnalyticsChat = chatType === "AIBLE" || chatType === "WHATS_CHANGED";
    const isProcessing = dataset?.status_name !== "DONE";
    const aiServiceModels = useSelector(getModels);
    const aiServiceModelsArr = Object.values(aiServiceModels);
    const modelOptions = aiServiceModelsArr.map((m) => ({ label: m?.name, value: m?.id }));
    const foundAgentIqBaseModel = modelOptions.find((m) => m.value === Number(agentIQBaseModel));
    const foundAgentIqNlqModel = modelOptions.find((m) => m.value === Number(agentIQNLQModel));
    const foundAgentIqReasoningModel = modelOptions.find((m) => m.value === Number(agentIQReasoningModel));
    const docSummReady = chatType !== "DOCUMENT" || artifactId !== undefined || textInput;
    const isSQLReady = chatType !== "SQL" ||
        (textInput && datasetId !== undefined && datasetId !== "UPLOAD_CSV" && !isProcessing) ||
        (textInput && datasetId === undefined);
    const isComparisonReady = chatType !== "WHATS_CHANGED" ||
        (datasetId !== undefined &&
            datasetId !== "UPLOAD_CSV" &&
            !isProcessing &&
            dataset?.outcome &&
            comparisonOptions?.referenceStart !== undefined) ||
        (datasetId === undefined && textInput);
    const comparisonReanalysis = chatType === "WHATS_CHANGED" &&
        dataset &&
        !isProcessing &&
        dataset?.outcome &&
        comparisonOptions?.dateField !== undefined &&
        comparisonOptions?.dateField !== dataset?.options?.timestamp_column;
    const isAnalyticsReady = chatType !== "AIBLE" ||
        (datasetId !== undefined && datasetId !== "UPLOAD_CSV" && !isProcessing && dataset?.outcome) ||
        (datasetId === undefined && textInput);
    const isAgentIqReady = (blueprintId == "AGENT_IQ" || chatType === "AGENT_IQ") &&
        foundAgentIqBaseModel &&
        foundAgentIqNlqModel &&
        foundAgentIqReasoningModel &&
        agentIqTools?.length;
    const hasIntro = (chatType === "AIBLE" || chatType === "WHATS_CHANGED") &&
        datasetId !== undefined &&
        datasetId !== "UPLOAD_CSV" &&
        !isProcessing &&
        dataset?.outcome;
    const hasUserPrompt = textInput || hasIntro;
    const isAskIntro = !textInput && hasIntro;
    const aaCurrent = useSelector((state) => aibleAnywhereReducer.getAibleAnywhere(state, "new"));
    const disableAsk = aaCurrent?.steps?.[0]?.Type === "Optimize"
        ? false
        : sProject !== undefined && datasetId !== undefined && datasetId !== "UPLOAD_CSV"
            ? false
            : blueprintId == "AGENT_IQ" || chatType === "AGENT_IQ"
                ? isAsking || isAskingAgent || !isAgentIqReady
                : isAsking ||
                    isAskingAgent ||
                    !hasUserPrompt ||
                    !docSummReady ||
                    !isSQLReady ||
                    !isComparisonReady ||
                    comparisonReanalysis ||
                    !isAnalyticsReady;
    const hasTooltip = !hasUserPrompt || !docSummReady || isAskIntro;
    const customBackgroundColor = ((location && location.pathname && location.pathname.indexOf("next") !== -1) || viewType === "new chat") &&
        chatApp?.wizard?.backgroundColor !== undefined &&
        chatApp?.wizard?.backgroundColor !== ""
        ? chatApp?.wizard.backgroundColor
        : "#FFFFFF";
    const linkColorDarkColor = "#1F181C";
    const buttonText = chatApp?.wizard?.chatButtonCompany ? `${chatApp?.wizard?.chatButtonCompany} AI` : "ChatAible";
    const chatButtonDarkColor = "#1F181C";
    const hasReplayPrompts = replayPrompts && replayPrompts.length > 0;
    const disableReplayAsk = sProject !== undefined && datasetId !== undefined
        ? false
        : isAsking ||
            (chatType === "DOCUMENT" && artifactId === undefined) ||
            (chatType === "SQL" && datasetId === undefined) ||
            comparisonReanalysis ||
            (chatType === "WHATS_CHANGED" && comparisonOptions?.referenceStart !== undefined) ||
            ((chatType === "AIBLE" || chatType === "WHATS_CHANGED") && datasetId === undefined);
    const IS_SIMPLE_MODE = true;
    const showSidePanel = Boolean(contextSelectionPanelTab) || showSettings === true;
    const isAgentBuilderLoading = 
    // Commenting out now that there is 'beta' access to step builder in Advanced Use Case settings
    // isEnabled("agent-autonomy-controls") &&
    // isEnabled("aible-agent") &&
    (agentAutonomyView === "LOW" || agentAutonomyView === "MODERATE") && isAibleAnywhereLoading;
    const datasetFields = Object.values(dataset?.fields ?? {});
    const retDatasetFields = dataset?.ranked_fields ?? datasetFields;
    const datasetDateOptions = retDatasetFields.filter((f) => f.type === "date");
    const handleSelectTemplate = (val) => {
        if (showSettings) {
            dispatch(updateShowSettings(false));
        }
        if (contextSelectionPanelTab) {
            dispatch(updateContextSelectionPanelTab(undefined));
        }
        updatedSelectedTemplate(val);
    };
    const submitQuestion = () => {
        if (!chatClearInput) {
            dispatch(updatePrompt(textInput, false));
        }
        updateContextSelectionPanelTab(undefined);
        if (blueprintId === "ASK_FOR_DATASET") {
            return dispatch(initAskForDataset(textInput));
        }
        updateIsChatLoading(true);
        return dispatch(handleInit(textInput, sProject, undefined, undefined, undefined, useAgentSimpleAnalytics && isAnalyticsChat));
    };
    const isFakeChat = conversationArray !== undefined && conversationArray.length > 0 && (chatType === "LLM" || chatType === "AIBLE");
    if (isFakeChat) {
        return React.createElement(FakeChat, null);
    }
    return (React.createElement("div", { className: styles.newChat, style: {
            backgroundColor: customBackgroundColor
        } },
        React.createElement(ChatTourContainer, null),
        React.createElement("div", { className: styles.newChatBody, style: showSidePanel ? { width: "60%" } : { width: "100%" } },
            IS_SIMPLE_MODE === true ? (React.createElement(NewChatSimpleHeader, null)) : (React.createElement(Header, { nextId: nextId, inputBoxRef: inputBoxRef, usecaseSelectorRef: usecaseSelectorRef, isWhatsChangedAnalysisLoading: isWhatsChangedAnalysisLoading, updateIsWhatsChangedAnalysisLoading: updateIsWhatsChangedAnalysisLoading })),
            isEnabled("enable-agents-templates-view") &&
                isEnabled("new-chat-top-sample-templates") &&
                agentAutonomyView === "RECOMMENDED" && (React.createElement(TopSampleTemplates, { selectedTemplate: selectedTemplate, updateSelectedTemplate: handleSelectTemplate })),
            isEnabled("chat-agent-iq") && agentAutonomyView === "HIGH" && React.createElement(AgentIqToolOptionsList, null),
            React.createElement("div", { className: styles.newChatActionsWrapper },
                React.createElement("div", { className: styles.newChatActions, id: "chat-message-box" },
                    useAgentSimpleAnalytics && isAnalyticsChat ? (React.createElement(NextSearchContainer, { id: nextId, isOnlySearch: true, isSimpleAgentAnalytics: true, hideMargins: true, isHideSearchType: true, searchSelectStyle: { marginLeft: "0px", width: "100%" }, style: { position: "relative", top: "0px", marginLeft: "0px" }, menuPlacement: "top", maxSearchOptionsDisplayed: 3, maxSelectedOptions: 3 })) : (React.createElement("textarea", { className: styles.chatPrompt, value: textInput, onChange: (val) => updateTextInput(val.target.value), placeholder: sProject !== undefined && convoReplay[sProject]
                            ? [...convoReplay[sProject]].reverse().reduce((a, b) => b?.userPrompt + "\n" + a, "")
                            : currentStep?.placeholderText
                                ? currentStep?.placeholderText
                                : agentAutonomyView === "LOW"
                                    ? "Define the flow on the right and test responses here"
                                    : agentAutonomyView === "MODERATE"
                                        ? "What type of agent would you like Aible to build?"
                                        : blueprintId === "CHATAIBLE_DATA_SEARCH"
                                            ? "What information are you looking for?"
                                            : blueprintId === "AGENT_IQ"
                                                ? "What would you like me to do?"
                                                : "Start Chatting", autoFocus: true, onKeyDown: (e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                if (!(disableAsk || isAsking || isChatLoading || isAgentBuilderLoading)) {
                                    submitQuestion();
                                }
                            }
                            if ((isMac && e.metaKey && e.key === "e") || (!isMac && e.ctrlKey && e.key === "e")) {
                                e.preventDefault();
                                const usecaseSelector = usecaseSelectorRef?.current;
                                if (usecaseSelector) {
                                    usecaseSelector.focus();
                                }
                            }
                            return;
                        }, ref: inputBoxRef })),
                    showDaterange &&
                        (chatType === "WHATS_CHANGED" || chatType === "KPI_ANALYSIS") &&
                        Boolean(datasetDateOptions?.length || comparisonOptions?.dateField) &&
                        Boolean(dataset) && React.createElement(WhatsChangedInput, { fullWidthRangeStyle: true }),
                    React.createElement("div", { className: styles.botActions },
                        React.createElement("div", { className: styles.botActionsLeftSideContainer },
                            1 > 2 &&
                                isEnabled("chat-suggest-questions") &&
                                (chatType === "DOCUMENT" || chatType === "AIBLE") &&
                                (isAskingSuggestQuestions ? (React.createElement(LoadingIndicator, { style: { margin: "0px", scale: ".6" } })) : (React.createElement(Button, { disabled: !((chatType === "DOCUMENT" && artifactId) || (chatType === "AIBLE" && datasetId)), onClick: () => {
                                        return dispatch(initSuggestQuestions(textInput));
                                    }, style: { marginRight: "10px" } }, "Suggest Questions"))),
                            isEnabled("auto-apply-chats") && hasReplayPrompts && !(isAsking || isChatLoading) && (React.createElement(Button, { disabled: disableReplayAsk, onClick: () => {
                                    dispatch(askMultiModels([{ value: modelId, label: modelId }]));
                                }, style: {
                                    color: linkColorDarkMode
                                        ? linkColorDarkColor
                                        : chatApp?.wizard?.linkColor !== undefined
                                            ? "#ffffff"
                                            : "",
                                    ...(chatApp?.wizard?.linkColor !== undefined
                                        ? {
                                            background: chatApp?.wizard?.linkColor,
                                            borderColor: chatApp?.wizard?.linkColor
                                        }
                                        : {}),
                                    marginRight: "10px",
                                    fontSize: "13px"
                                } }, "Ask Saved Questions")),
                            !(blueprintId === "AGENT_IQ") && (React.createElement(ContextSelectionButtons, { nextId: "new", checkFolderData: true, checkAgentAutonomy: true, showDaterange: showDaterange, setShowDaterange: setShowDaterange })),
                            isEnabled("chat-agent-iq") && (React.createElement(React.Fragment, null,
                                isEnabled("agent-autonomy-controls") ? (agentAutonomyView === "HIGH" || blueprintId === "AGENT_IQ" ? (React.createElement("div", { className: styles.agentIQAutoDiscoveryWrapper },
                                    React.createElement("img", { src: sparklesImg }),
                                    "Auto-discovery enabled")) : (React.createElement(React.Fragment, null))) : (React.createElement(Button, { style: { marginLeft: "10px" }, onClick: () => dispatch(updateBlueprintId("AGENT_IQ")), active: blueprintId === "AGENT_IQ" }, "NVIDIA NeMo agent")),
                                blueprintId === "AGENT_IQ_TEAM" && (React.createElement(Button, { active: isWebSearch === true, onClick: () => dispatch(updateIsWebSearch(!isWebSearch)) }, "Web Search")))),
                            React.createElement(OverlayTrigger, { placement: "top", trigger: "click", rootClose: true, overlay: React.createElement(Popover, { id: "popover-positioned-top", bsClass: "chat-tones-popover popover" },
                                    React.createElement("div", { style: { display: "flex", flexFlow: "column", padding: "5px 0" } },
                                        React.createElement("div", { style: { fontWeight: 700, marginBottom: "4px", padding: "0 10px" } }, "Select a tone"),
                                        React.createElement("div", { style: { height: "150px", overflowX: "hidden" } },
                                            React.createElement("div", { style: {
                                                    width: "100%",
                                                    height: "100%",
                                                    overflowX: "scroll",
                                                    display: "flex",
                                                    flexFlow: "column"
                                                } }, chatTones?.length > 0 &&
                                                chatTones.map(({ label, value }) => (React.createElement("a", { style: { padding: "5px 10px" }, className: clsx(styles.chatToneItem, value === chatTone && styles.active), onClick: () => dispatch(updateChatTone(value)) }, label))))),
                                        chatTones?.length === 0 && React.createElement("span", { style: { padding: "5px 10px" } }, "No available tones"))) },
                                React.createElement(Button, { style: { padding: "9px 11px" } },
                                    React.createElement("img", { src: PenIcon })))),
                        React.createElement("div", null,
                            React.createElement(Tooltip, { trigger: hasTooltip ? ["hover"] : [], overlay: !docSummReady
                                    ? "Please select the document set you would like ChatAible to summarize."
                                    : comparisonReanalysis
                                        ? "Please reanalyze the dataset to enable questions about what's changed."
                                        : !isSQLReady || !isComparisonReady
                                            ? "Please select the dataset you would like to ask questions about."
                                            : isAskIntro
                                                ? "Leave the question field blank, and ChatAible will summarize the entire dataset. Alternatively, you can ask a specific question to start your analytics chat."
                                                : !hasUserPrompt
                                                    ? "Please ask any question to begin your chat."
                                                    : "Loading..." },
                                React.createElement(Button, { type: "primary", style: {
                                        fontSize: "13px",
                                        color: chatButtonDarkMode ? chatButtonDarkColor : "",
                                        ...(chatApp?.wizard?.chatButtonColor !== undefined
                                            ? {
                                                background: chatApp?.wizard?.chatButtonColor,
                                                borderColor: chatApp?.wizard?.chatButtonColor
                                            }
                                            : {})
                                    }, className: chatApp?.wizard?.chatButtonColor !== undefined ? "" : "", isLoading: isAsking || isChatLoading || isAgentBuilderLoading, disabled: disableAsk, onClick: () => {
                                        submitQuestion();
                                    } },
                                    isAskIntro ? `Summarize` : `Ask`,
                                    React.createElement(FontAwesomeIcon, { icon: faArrowRight, style: { marginLeft: "10px" } })))))),
                React.createElement(SqlWarningPopup, { isNewChat: true }),
                React.createElement(LegacyModelWarning, { isNewChat: true })),
            isEnabled("agent-autonomy-controls") && React.createElement(AgentAutonomyControls, null),
            React.createElement("div", { className: styles.newChatBottomSpacer })),
        showSettings ? (React.createElement("div", { className: styles.newChatSettings },
            React.createElement(ChatGPTSettings, { nextId: "new" }))) : contextSelectionPanelTab ? (React.createElement("div", { className: styles.rightDataPanel },
            React.createElement(ContextSelectionPanel, { nextId: "new", allowAgents: true, isClose: true }))) : newChatKPIPanel ? (React.createElement("div", { className: styles.rightDataPanel, style: { borderLeft: "0px solid transparent", padding: "20px" } },
            React.createElement(DatasetAnalytics, { defaultShow: true, skipRedirect: true, skipExploreStepDetection: true }))) : agentAutonomyView === "LOW" || agentAutonomyView === "MODERATE" ? (React.createElement("div", { className: styles.newChatAibleAnywhereContainer },
            React.createElement(AibleAgent, { isNewChat: true }))) : isEnabled("new-chat-top-sample-templates") && selectedTemplate ? (React.createElement(TemplatesRightPanel, { selectedItem: selectedTemplate, updateSelectedItem: handleSelectTemplate, rightPanelStyle: { bottom: "unset", top: "0px", height: "100%", zIndex: "200" } })) : (React.createElement(React.Fragment, null))));
};
export default NewChat;
